import { useCallback, useEffect, useMemo, useState } from "react";

import useAuth from "@/contexts/auth-context";
import { useNotification } from "@/contexts/notification-context";
import { IConversationTransferStep } from "@/interfaces/conversation-transfer.interfaces";
import conversationService from "@/services/conversation.service";
import { UserProfile } from "@/types/user.types";

function hasAssignedAttendant(userId: number | null | undefined): userId is number {
  return userId != null && userId > 0;
}

export function useChatAttendanceActions() {
  const { user } = useAuth();
  const { selectedChat, setSelectedChat, updateChatInList } = useNotification();
  const [transferSteps, setTransferSteps] = useState<IConversationTransferStep[]>([]);
  const [isLoadingTransferSteps, setIsLoadingTransferSteps] = useState(false);

  const isAdmin = useMemo(
    () => user?.userProfile?.some((p) => p.profileId === UserProfile.ADMINISTRATOR) ?? false,
    [user?.userProfile]
  );

  const assignedUserId = selectedChat?.userId;
  const isOwner =
    hasAssignedAttendant(assignedUserId) && user?.id != null && assignedUserId === user.id;

  const chatInputEnabled = isOwner;
  const canFinish = chatInputEnabled;
  const canAssume =
    !hasAssignedAttendant(assignedUserId) ||
    (user?.id != null && assignedUserId !== user.id && isAdmin);

  useEffect(() => {
    let cancelled = false;

    const loadSteps = async () => {
      setIsLoadingTransferSteps(true);
      try {
        const steps = await conversationService.getTransferSteps();
        if (!cancelled) setTransferSteps(steps);
      } catch {
        if (!cancelled) setTransferSteps([]);
      } finally {
        if (!cancelled) setIsLoadingTransferSteps(false);
      }
    };

    loadSteps();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedChat) {
      return;
    }

    const { conversationId } = selectedChat;
    let cancelled = false;

    conversationService.getConversation(conversationId).then((response) => {
      if (cancelled) return;
      setSelectedChat((prev) => {
        if (!prev || prev.conversationId !== conversationId) return prev;
        return {
          ...prev,
          userId: response.userId,
          userName: response.userName,
          workflowId: response.workflowId,
          workflowName: response.workflowName,
        };
      });
    });

    return () => {
      cancelled = true;
    };
  }, [selectedChat?.conversationId, setSelectedChat]);

  const assumeControl = useCallback(async () => {
    if (!selectedChat) return;
    const { conversationId } = selectedChat;

    await conversationService.takeConversation(conversationId);

    const updatedChat = {
      ...selectedChat,
      userId: user?.id || 0,
      userName: user?.name || "",
      workflowName: null,
      workflowId: null,
    };

    await updateChatInList(updatedChat);
    setSelectedChat(updatedChat);
  }, [selectedChat, user, updateChatInList, setSelectedChat]);

  const finishControl = useCallback(async () => {
    if (!selectedChat) return;
    const { conversationId } = selectedChat;

    await conversationService.finishConversation(conversationId);

    const updatedChat = {
      ...selectedChat,
      userId: null,
      userName: "",
      workflowName: "",
      workflowId: null,
    };

    await updateChatInList(updatedChat);
    setSelectedChat(updatedChat);
  }, [selectedChat, updateChatInList, setSelectedChat]);

  const transferToStep = useCallback(
    async (step: IConversationTransferStep) => {
      if (!selectedChat || !chatInputEnabled) return;
      const { conversationId } = selectedChat;

      await conversationService.gotoConversationStep(conversationId, {
        stepName: step.stepName,
        workflowId: step.workflowId,
      });

      const updatedChat = {
        ...selectedChat,
        userId: null,
        userName: "",
        workflowId: step.workflowId,
        workflowName: step.workflowName,
      };

      await updateChatInList(updatedChat);
      setSelectedChat(updatedChat);
    },
    [selectedChat, chatInputEnabled, updateChatInList, setSelectedChat]
  );

  return {
    chatInputEnabled,
    canAssume,
    canFinish,
    transferSteps,
    isLoadingTransferSteps,
    assumeControl,
    finishControl,
    transferToStep,
  };
}
